import { PaymentLineItemModel } from '../PaymentIntentModel';
import { PricingUtil } from '../utils/pricing/pricing.util';

/** Snapshot of `QuickbooksService.prepareBillPreview` for display-only storage. */
export interface CgQbBillPreviewSnapshot {
  lineItems: PaymentLineItemModel[];
  grandTotal: number;
  grandTotalFormatted: string;
  summaryReportAppointmentsTotal: number;
  summaryReportAppointmentsTotalFormatted: string;
  billAppointmentsSubtotal: number;
  billAppointmentsSubtotalFormatted: string;
  appointmentsMatchSummaryReport: boolean;
  appointmentsVsSummaryDelta: number;
  appointmentsVsSummaryDeltaFormatted: string;
  vendorDisplayName: string;
  vendorEmail?: string;
  weekStartDate: string;
  weekEndDate: string;
  existingQuickBooksId?: string;
}

export interface CgWeekReportOutput {
  _id?: unknown;
  cgId: string;
  weekStartDate: string;
  weekEndDate: string;
  weekReportId: string;
  quickBooksId?: string;
  storedAt: Date;
  preview: CgQbBillPreviewSnapshot;
}

export function roundMoneyTwoDecimals(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Draft row for admin editing payroll snapshot lines (numeric amount). */
export interface CgAdminPayrollLineDraft {
  desc: string;
  amount: number;
}

export function parseCurrencyDisplayToNumber(display: string): number {
  const t = (display ?? '').replace(/[^0-9.-]/g, '');
  const n = parseFloat(t);
  return Number.isFinite(n) ? roundMoneyTwoDecimals(n) : 0;
}

export function paymentLineItemsAndGrandTotalFromAdminDraft(
  items: CgAdminPayrollLineDraft[],
): { lineItems: PaymentLineItemModel[]; grandTotal: number } {
  const filtered = items.filter((x) => (x.desc ?? '').trim().length > 0);
  const grandTotal = roundMoneyTwoDecimals(
    filtered.reduce(
      (s, x) => s + roundMoneyTwoDecimals(Number(x.amount) || 0),
      0,
    ),
  );
  const lineItems: PaymentLineItemModel[] = filtered.map((x) => ({
    desc: x.desc.trim(),
    amount: PricingUtil.toCurrency(
      roundMoneyTwoDecimals(Number(x.amount) || 0),
    ),
  }));
  return { lineItems, grandTotal };
}

export function mergeCgQbPreviewAfterAdminLineEdit(
  preview: CgQbBillPreviewSnapshot,
  lineItems: PaymentLineItemModel[],
  grandTotal: number,
): CgQbBillPreviewSnapshot {
  const formatted = PricingUtil.toCurrency(grandTotal);
  const zeroFmt = PricingUtil.toCurrency(0);
  return {
    ...preview,
    lineItems,
    grandTotal,
    grandTotalFormatted: formatted,
    billAppointmentsSubtotal: grandTotal,
    billAppointmentsSubtotalFormatted: formatted,
    summaryReportAppointmentsTotal: grandTotal,
    summaryReportAppointmentsTotalFormatted: formatted,
    appointmentsMatchSummaryReport: true,
    appointmentsVsSummaryDelta: 0,
    appointmentsVsSummaryDeltaFormatted: zeroFmt,
  };
}
